import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Handshake, Users, Eye, BadgeIndianRupee, FileCheck2 } from "lucide-react";
import { WhatsAppIcon } from "@/components/site/WhatsAppIcon";

const ease = [0.22, 1, 0.36, 1];

const whyPartner = [
  {
    icon: Users,
    title: "Your clients stay yours",
    body: "We handle structuring, lender negotiation and documentation. The client relationship — and the credit for the outcome — stays with you.",
  },
  {
    icon: BadgeIndianRupee,
    title: "Transparent referral commercials",
    body: "A clear, pre-agreed referral payout on every successful disbursal. No ambiguity, no delayed settlements.",
  },
  {
    icon: Eye,
    title: "Visibility on every referral",
    body: "Each referral gets a reference ID and status updates, so you always know where your client's file stands.",
  },
  {
    icon: FileCheck2,
    title: "Institutional-grade execution",
    body: "From working capital and term loans to LAP, home loans and invoice discounting — your clients get the same rigour corporates do.",
  },
];

const who = [
  "Chartered Accountants",
  "Company Secretaries",
  "Loan & mortgage DSAs",
  "Real-estate consultants",
  "Wealth managers & IFAs",
  "Business consultants",
];

const steps = [
  { no: "01", title: "Refer", body: "Share your client's requirement with us — a call, WhatsApp message or the enquiry form with their details." },
  { no: "02", title: "We assess", body: "We evaluate the file, speak to the client with your introduction, and confirm what is achievable." },
  { no: "03", title: "We place", body: "We match, negotiate and drive the file through documentation to sanction and disbursal." },
  { no: "04", title: "You earn", body: "Your referral payout is settled promptly on disbursal — and your client remembers who connected them." },
];

export default function Partners() {
  return (
    <div data-testid="partners-page" className="min-h-screen bg-night text-foreground">
      <header className="border-b border-white/10 bg-night/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" data-testid="partners-brand" className="flex items-center">
            <span className="bg-white rounded-md px-3 py-1.5 flex items-center shadow-md shadow-black/30">
              <img src="/globus-lockup.png" alt="Globus Financial Services" className="h-7 w-auto object-contain" />
            </span>
          </Link>
          <Link data-testid="partners-back-link" to="/" className="text-sm text-[#A0AAB2] hover:text-gold transition-colors flex items-center gap-2">
            <ArrowLeft size={14} /> Back to site
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-10">
        <motion.section
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease }}
          className="py-16"
        >
          <p className="overline-label mb-4">Referral Partnerships</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl leading-[1.08]">
            Your clients need capital. <span className="text-gold italic">Together, we deliver it.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base md:text-lg text-[#A0AAB2] leading-relaxed">
            A referral partnership for CAs, CSs, DSAs and consultants whose clients ask about loans.
            You make the introduction — we do the heavy lifting and share the outcome with you.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <a
              data-testid="partners-whatsapp-cta"
              href="https://wa.me/918796512777?text=Hi%20Globus%20Financial%20Services%2C%20I%27d%20like%20to%20discuss%20a%20referral%20partnership."
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gold text-night hover:bg-goldhover transition-colors rounded-sm px-7 py-3.5 font-semibold flex items-center gap-2"
            >
              <WhatsAppIcon size={18} /> Discuss a partnership
            </a>
            <Link
              data-testid="partners-enquire-cta"
              to="/#enquire"
              className="border border-gold text-gold hover:bg-gold/10 transition-colors rounded-sm px-7 py-3.5 font-medium flex items-center gap-2"
            >
              Refer a client <ArrowUpRight size={16} />
            </Link>
          </div>
        </motion.section>

        <section className="pb-16">
          <p className="overline-label mb-10">Why partner with us</p>
          <div className="grid sm:grid-cols-2 gap-6">
            {whyPartner.map((w, i) => (
              <motion.div
                key={w.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.08, ease }}
                data-testid={`partner-benefit-${i}`}
                className="bg-slate2 border border-white/10 p-8"
              >
                <w.icon size={24} className="text-gold mb-5" />
                <h2 className="font-serif text-xl sm:text-2xl mb-3">{w.title}</h2>
                <p className="text-sm text-[#A0AAB2] leading-relaxed">{w.body}</p>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="py-16 border-t border-white/10">
          <p className="overline-label mb-10">How the partnership works</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((s, i) => (
              <motion.div
                key={s.no}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.08, ease }}
                data-testid={`partner-step-${s.no}`}
              >
                <span className="font-serif text-5xl text-gold/30">{s.no}</span>
                <h3 className="font-serif text-xl mt-4 mb-3">{s.title}</h3>
                <p className="text-sm text-[#A0AAB2] leading-relaxed">{s.body}</p>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="py-16 border-t border-white/10">
          <p className="overline-label mb-8">Who this is for</p>
          <div className="flex flex-wrap gap-3" data-testid="partner-audience">
            {who.map((w) => (
              <span key={w} className="border border-white/15 text-[#A0AAB2] text-sm px-5 py-2.5 rounded-sm flex items-center gap-2">
                <Handshake size={14} className="text-gold" /> {w}
              </span>
            ))}
          </div>
          <p className="mt-10 text-xs text-muted-foreground leading-relaxed max-w-2xl">
            Referral arrangements are subject to applicable regulations and lender policies. Client
            information shared with us is handled under our <Link to="/privacy-policy" className="text-gold hover:underline">Privacy Policy</Link>.
          </p>
        </section>
      </main>

      <footer className="border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-6 text-xs text-muted-foreground">
          © {new Date().getFullYear()} Globus Financial Services · Sahibabad, Uttar Pradesh 201005, India · GSTIN: 09AEFPK4400F1ZW
        </div>
      </footer>
    </div>
  );
}
